import { gql } from '@apollo/client';
import { Notice } from '@mnd-frontend/graphql-schema';
import IconAnalyze from '@mnd-frontend/icons/components/Analyze';
import IconCheck from '@mnd-frontend/icons/components/Check';
import IconReach from '@mnd-frontend/icons/components/Reach';
import IconSearch from '@mnd-frontend/icons/components/Search';
import { Button } from '@mnd-frontend/ui/publish/components';
import { capitalize } from 'lodash-es';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TrialPopover } from '../../components/TrialPopover';
import { useCurrentPressroom } from '../../contexts/CurrentPressroomContext';
import { useTrialUserDetailsQuery } from '../../generated/hooks';
import { track } from '../../lib/tracker';
import ContactBookingModal from './ContactBookingModal';

const DAYS_OF_TRIAL = 10;

gql`
  query TrialUserDetails($sourceId: ID!, $pressroomId: ID!) {
    me {
      id
      user {
        id
        email
        phone
      }
    }
    pressroom(id: $pressroomId) {
      id
      pendingNotices {
        id
        trialDemo
      }
    }
    source(id: $sourceId) {
      id
      currentPlan
      trialDaysLeft
    }
  }
`;

const HeaderPillMenu = ({
  setDisableOutsideClick,
  closeTrialMenu,
}: {
  setDisableOutsideClick: (v: boolean) => void;
  closeTrialMenu: () => void;
}) => {
  const { t } = useTranslation();
  const [showBookingModal, setShowBookingModal] = useState(false);
  const { sourceId, pressroomId } = useCurrentPressroom();
  const { data } = useTrialUserDetailsQuery({
    variables: {
      pressroomId,
      sourceId,
    },
  });

  const demoBooked = !data?.pressroom?.pendingNotices.trialDemo;
  const capitalizedPlan = capitalize(data?.source?.currentPlan);
  const trialDaysLeft = data?.source?.trialDaysLeft || 0;

  const openModal = () => {
    track({
      event: 'Trial Menu - CTA Clicked',
      traits: {
        cta_text: t('trial_book_demo'),
      },
    });
    setDisableOutsideClick(true);
    setShowBookingModal(true);
  };

  const closeModal = () => {
    setDisableOutsideClick(false);
    setShowBookingModal(false);
  };

  return (
    <>
      <TrialPopover
        trialDaysLeft={trialDaysLeft}
        totalTrialDays={DAYS_OF_TRIAL}
        header={t('trial_youre_on_free_trial')}
        body={
          <Trans
            i18nKey={'trial_pro_plan_access'}
            values={{
              plan: capitalizedPlan,
            }}
          />
        }
        usps={[
          {
            icon: <IconReach />,
            text: t('trial_usp_reach'),
          },
          {
            icon: <IconAnalyze />,
            text: t('trial_usp_analyze'),
          },
          {
            icon: <IconSearch />,
            text: t('trial_usp_seo'),
          },
        ]}
      >
        <Button
          type="button"
          onClick={openModal}
          disabled={demoBooked}
          leadingIcon={demoBooked ? <IconCheck /> : null}
        >
          {demoBooked ? t('trial_demo_booked') : t('trial_book_demo')}
        </Button>
      </TrialPopover>
      {data && (
        <ContactBookingModal
          show={showBookingModal}
          defaultValues={{
            email: data.me?.user.email || '',
            phone: data.me?.user.phone || '',
          }}
          close={closeModal}
          onSuccessClose={() => {
            closeModal();
            closeTrialMenu();
          }}
          hubspotType="TRIAL_CUSTOMER_UPGRADE"
          noticeKey={Notice.TrialDemo}
          tracking={{
            event: 'Trial Modal - form submitted',
            traits: {
              cta_text: t('trial_book_demo'),
            },
          }}
        />
      )}
    </>
  );
};

export default HeaderPillMenu;
